import { Button, Tooltip } from "antd";
import { FC, MouseEvent, ReactNode } from "react";
import { LuSearch } from "react-icons/lu";
import { DraftBinding } from "./draft";
import { Offer, offerGroups, offerNotes, SUGGEST_TEXTS } from "./flow";

interface Props {
  /** The id of the list; the editor's text box points at it (aria-controls). */
  listId: string;
  /** null while the search (ObjectSearch.tsx) has no answer yet: then only its head and notes show. */
  offer: Offer | null;
  /** The active option, -1 for none; the index after the last object is "Search all of XorMon". */
  active: number;
  /** Whether the last row searches all of XorMon (only while the offer was asked for the last answer). */
  searchAll?: boolean;
  onPick: (index: number) => void;
  onSearchAll?: () => void;
  /** Drawn above the objects: the search's own field (ObjectSearch.tsx); absent for the offer while typing. */
  head?: ReactNode;
  /** More quiet lines under the objects ("Type at least 2 characters"). */
  moreNotes?: string[];
  /** What the list is, for a screen reader. */
  label?: string;
}

/** The id of one option, for aria-activedescendant on the text box. */
export function optionId(listId: string, index: number): string {
  return `${listId}-option-${index}`;
}

export function searchAllId(listId: string): string {
  return `${listId}-option-all`;
}

/**
 * A press anywhere in the list (or on the Add object button) keeps the cursor where it is (the editor, or the
 * search's field), so a pick lands where the person was writing and the words at the cursor are still the words asked
 * for; a press in the search's field is its own, to place the cursor in it.
 */
const keepFocus = (event: MouseEvent) => {
  if ((event.target as { tagName?: string } | null)?.tagName !== "INPUT") {
    event.preventDefault();
  }
};

/** A name cut where the asked words stand in it (case aside, the first time): before, the words, after. */
export interface MatchParts {
  before: string;
  hit: string;
  after: string;
}

/** Where the asked words stand in an object's name, to mark them in the list; null when they do not stand in it. */
export function matchParts(label: string, fragment: string): MatchParts | null {
  const words = (fragment || "").trim();
  if (!label || !words) {
    return null;
  }
  const at = label.toLowerCase().indexOf(words.toLowerCase());
  // a lower case that changes the length (a rare letter) would cut the name in the wrong place
  if (at < 0 || label.toLowerCase().length !== label.length) {
    return null;
  }
  return { before: label.slice(0, at), hit: label.slice(at, at + words.length), after: label.slice(at + words.length) };
}

/** An object's name with the asked words in it marked, so it shows why the object was found. */
const MarkedName: FC<{ label: string; fragment: string }> = ({ label, fragment }) => {
  const parts = matchParts(label, fragment);
  return parts ? <>{parts.before}<strong className="xm-chat-suggest-hit">{parts.hit}</strong>{parts.after}</> : <>{label}</>;
};

// W3C combobox pattern: focus stays in the text box, arrows/Enter/Escape drive the list. No option
// is active on open, so Enter still sends the message.
export const SuggestionList: FC<Props> = ({
  listId, offer, active, searchAll: searchAllProp, onPick, onSearchAll, head, moreNotes: moreNotesProp, label: labelProp,
}) => {
  // defaulted in the body: the React Compiler skips a component with a default in its destructured props
  const searchAll = searchAllProp ?? false;
  const moreNotes = moreNotesProp ?? [];
  const label = labelProp ?? "Objects matching the words at the cursor";
  const groups = offerGroups(offer);
  const notes = [...offerNotes(offer), ...moreNotes];
  const allIndex = offer?.items.length ?? 0;
  return <div className={head ? "xm-chat-suggest-popup xm-chat-object-search" : "xm-chat-suggest-popup"} onMouseDown={keepFocus}>
    {head}
    <div id={listId} role="listbox" aria-label={label} className="xm-chat-suggest-list">
      {groups.map((group, at) => {
        const headingId = `${listId}-group-${at}`;
        return <div key={headingId} role="group" aria-labelledby={headingId}>
          <div id={headingId} role="presentation" className="xm-chat-suggest-group">{group.title}</div>
          {group.options.map(({ index, item }) => <div
            key={`${index}-${item.target.item_id}`}
            id={optionId(listId, index)}
            role="option"
            aria-selected={active === index}
            className={active === index ? "xm-chat-suggest-item xm-chat-suggest-item-active" : "xm-chat-suggest-item"}
            onClick={() => onPick(index)}
          >
            <span className="xm-chat-suggest-item-name"><MarkedName label={item.label} fragment={offer?.fragment ?? ""} /></span>
            {item.distinguish.length > 0 && <span className="xm-chat-suggest-item-meta">{item.distinguish.join(" · ")}</span>}
          </div>)}
        </div>;
      })}
      {searchAll && <div
        id={searchAllId(listId)}
        role="option"
        aria-selected={active === allIndex}
        className={active === allIndex ? "xm-chat-suggest-item xm-chat-suggest-all xm-chat-suggest-item-active" : "xm-chat-suggest-item xm-chat-suggest-all"}
        onClick={onSearchAll}
      >
        {SUGGEST_TEXTS.searchAll}
      </div>}
    </div>
    {notes.map((note) => <div key={note} className="xm-chat-suggest-note">{note}</div>)}
  </div>;
};

/** "app-prod (Namespace · Kubernetes · SIM - kubernetes)": the name and the words that tell it from its namesakes. */
export function selectedText(binding: DraftBinding): string {
  const words = binding.shown.distinguish.filter((word) => word);
  return words.length ? `${binding.shown.label} (${words.join(" · ")})` : binding.shown.label;
}

// Shows which object each picked name resolves to; a refused pick stays struck through until
// re-picked or dropped, and is never sent.
export const SelectedRow: FC<{
  /** The picks of the draft, in the order of the text. */
  bindings: DraftBinding[];
  /** Drops the pick; its words stay in the text. */
  onRemove: (index: number) => void;
  /** Offers objects again for the words of a refused pick. */
  onPickAgain: (index: number) => void;
}> = ({ bindings, onRemove, onPickAgain }) => {
  if (!bindings.length) {
    return null;
  }
  return <div className="xm-chat-selected-row" aria-label="Selected objects">
    <span className="xm-chat-selected-row-label">Selected:</span>
    {bindings.map((binding, index) => {
      const text = selectedText(binding);
      return <span
        key={`${binding.range.start}-${binding.target.item_id}`}
        className={binding.struck ? "xm-chat-selected-row-chip xm-chat-selected-row-struck" : "xm-chat-selected-row-chip"}
      >
        {binding.struck ? <s>{text}</s> : <span>{text}</span>}
        {binding.struck && <button type="button" className="xm-chat-selected-row-again" onClick={() => onPickAgain(index)}>
          pick again
        </button>}
        <button
          type="button"
          className="xm-chat-selected-row-remove"
          aria-label={`Remove the selection of ${binding.shown.label}`}
          title="Remove the selection; the text stays"
          onClick={() => onRemove(index)}
        >
          ×
        </button>
      </span>;
    })}
  </div>;
};

// Asks immediately for objects at the cursor, even for a single character or a word the offer was shut for.
export const SuggestButton: FC<{
  disabled: boolean;
  /** What the button does, or why it cannot: shown as its tooltip. */
  reason: string;
  onPress: () => void;
}> = ({ disabled, reason, onPress }) => <Tooltip title={reason} placement="topLeft">
  {/* A disabled button hears no pointer, so the tooltip hangs on a wrapper that does. */}
  <span className="xm-chat-suggest-button-wrap">
    <Button
      type="text"
      size="small"
      className="xm-chat-suggest-button"
      aria-label="Add an object to the question"
      icon={<LuSearch />}
      disabled={disabled}
      onMouseDown={keepFocus}
      onClick={onPress}
    >
      {/* the magnifier says it looks for one, the words what becomes of it: it is added to the question */}
      Add object
    </Button>
  </span>
</Tooltip>;
